import React from "react";
import "../index.css";
import type { RuleOptionSelector } from "./ruleOptionSelectors";

// Single option box shared by the interactive picker (Create Rule) and the
// read-only summary (My Rules): selected options get the blue highlight,
// deselected ones get a red diagonal "X" through the box.
interface OptionBoxProps {
  label: string;
  on: boolean;
  onClick?: () => void;
}

const OptionBox: React.FC<OptionBoxProps> = ({ label, on, onClick }) => {
  const interactive = onClick !== undefined;
  return (
    <button
      type="button"
      className="font_family_base"
      aria-pressed={on}
      disabled={!interactive}
      onClick={onClick}
      style={{
        position: "relative",
        padding: "8px 4px",
        borderRadius: 6,
        border: `2px solid ${on ? "#4a9eff" : "#555"}`,
        background: on ? "#1a3a5c" : "transparent",
        color: on ? "#4a9eff" : "#9ca3af",
        fontSize: 13,
        fontWeight: on ? 700 : 400,
        cursor: interactive ? "pointer" : "default",
        overflow: "hidden",
      }}
    >
      {label}
      {!on && (
        // Diagonal-line "X" over deselected options. Two thin bars, each
        // long enough to span the box's diagonal, rotated to cross through
        // the center.
        <span
          aria-hidden="true"
          style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
        >
          <span
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              width: "140%",
              height: 2,
              background: "#e5484d",
              opacity: 0.85,
              transform: "translate(-50%, -50%) rotate(45deg)",
            }}
          />
          <span
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              width: "140%",
              height: 2,
              background: "#e5484d",
              opacity: 0.85,
              transform: "translate(-50%, -50%) rotate(-45deg)",
            }}
          />
        </span>
      )}
    </button>
  );
};

interface RuleOptionMultiSelectProps {
  selector: RuleOptionSelector;
  selected: string[];
  onChange: (selected: string[]) => void;
}

const RuleOptionMultiSelect: React.FC<RuleOptionMultiSelectProps> = ({
  selector,
  selected,
  onChange,
}) => {
  const toggle = (value: string) =>
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : // keep option order stable regardless of click order
          selector.options
            .map((o) => o.value)
            .filter((v) => v === value || selected.includes(v)),
    );

  const allSelected = selected.length === selector.options.length;

  return (
    <div style={{ maxWidth: 420, margin: "8px auto 0", padding: "0 16px" }}>
      <p
        className="font_family_base"
        style={{ fontSize: 15, textAlign: "center", marginBottom: 10 }}
      >
        {selector.prompt}
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${selector.columns}, 1fr)`,
          gap: 8,
        }}
      >
        {selector.options.map((option) => (
          <OptionBox
            key={option.value}
            label={option.label}
            on={selected.includes(option.value)}
            onClick={() => toggle(option.value)}
          />
        ))}
      </div>
      <div style={{ textAlign: "center", marginTop: 10 }}>
        <button
          type="button"
          className="font_family_base"
          onClick={() =>
            onChange(allSelected ? [] : selector.options.map((o) => o.value))
          }
          style={{
            background: "none",
            border: "none",
            color: "#4a9eff",
            fontSize: 13,
            cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          {allSelected ? "Clear all" : "Select all"}
        </button>
      </div>
    </div>
  );
};

export default RuleOptionMultiSelect;

// Read-only version for My Rules: same grid and X-out styling, no prompt,
// no Select all/Clear all, and the boxes aren't clickable.
interface RuleOptionSummaryProps {
  selector: RuleOptionSelector;
  selected: string[];
}

export const RuleOptionSummary: React.FC<RuleOptionSummaryProps> = ({
  selector,
  selected,
}) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: `repeat(${selector.columns}, 1fr)`,
      gap: 8,
      maxWidth: 420,
      margin: "10px auto 0",
    }}
  >
    {selector.options.map((option) => (
      <OptionBox
        key={option.value}
        label={option.label}
        on={selected.includes(option.value)}
      />
    ))}
  </div>
);
